import { useEffect, useState } from "react";

import { DateTime } from "luxon";
import { useDrop } from "react-dnd";

import { MinusIcon } from "@heroicons/react/24/outline";

import LeadEvent from "./LeadEvent";
import useLeads from "../../data/useLeads";
import useTrips from "../../data/useTrips";
import TimeSlotSelect from "./TimeSlotSelect";
import classNames from "../../utils/classNames";
import { ICalendarTrip } from "../../models/Trip";
import { DragTypes } from "../../models/DragTypes";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { IAllLeadsResponse, ILead, LeadStatus, PaymentStatus } from "../../models/Lead";
import { Distances } from "../../pages/Calendar/TripManager";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useDriverTracking from "../../data/useDriverTracking";
import useDriverTrackingLatest from "../../data/useDriverTrackingLatest";

type IProps = {
  trip: ICalendarTrip;
  date: string;
  capacity: number;
  distances: Distances | undefined;
  selectedTrip?: string;
  setSelectedTrip?: any;
  handleSelected?: any;
  unavailable: boolean;
  year?: number;
  week?: number;
  leadsFromIds: IAllLeadsResponse | undefined;
  searchTerm?: string;
  showCollectedFilter?: boolean;
  showNotPaidFilter?: boolean;
  onDriverAvatarClick: (trip: ICalendarTrip) => void;
  tripColour?: string;
  // The pickup-number badge and trip-colour border are optimisation visuals, so
  // they only show when the Trip Optimisation feature flag is enabled.
  optimisationEnabled?: boolean;
};

export default function TripSlot({ onDriverAvatarClick, ...props }: IProps) {
  const { update } = useLeads(true, "TripSlot");
  const {
    update: updateTrip,
    remove,
    updateTimeslot,
  } = useTrips(props.year!, props.week!);
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");
  const [leads, setLeads] = useState<ILead[]>([]);
  const [removeOpen, setRemoveOpen] = useState(false);

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: DragTypes.EVENT,
      drop: (e: ILead) => handleDroppedLead(e),
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
      }),
    }),
    [props.trip]
  );

  const handleDroppedLead = (lead: ILead) => {
    // console.log("dropped lead", lead)

    if (lead === undefined) {
      saveHandlers.onError();
      return;
    }

    let updatedTrip = {
      leadId: lead.id,
      tripId: props.trip.id,
      driverId: props.trip.driverId,
      scheduledCollectionOn: props.date,
      order: leads.length,
    };

    updateTrip.mutate(updatedTrip, {
      onSuccess: () => {
        props.handleSelected(undefined);
        saveHandlers.onSuccess();
      },
      onError: saveHandlers.onError,
    });
  };

  const handleColour = () => {
    let background = "bg-gray-50";

    if (props.trip.id == props.selectedTrip) background = "bg-green-200";

    if (props.unavailable) background = "bg-gray-300";

    if (isOver) background = "bg-primary-200";

    return background;
  };

  const handleRemoveTrip = () => {
    remove.mutate(props.trip.id, {
      onSuccess: () => {
        setRemoveOpen(false);
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  const handleOrderChange = (up: boolean, lead: ILead) => {
    // Reorder by list position (not by arithmetic on `order`) so it works even
    // when order values aren't contiguous, then swap the two neighbours' order
    // values.
    const ordered = leads
      .filter((l: ILead) => l.status !== LeadStatus["Cancelled"])
      .slice()
      .sort((a: ILead, b: ILead) => a.order - b.order);

    const idx = ordered.findIndex((l: ILead) => l.id === lead.id);
    if (idx < 0) return;

    const swapIdx = up ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= ordered.length) return;

    const current = ordered[idx];
    const target = ordered[swapIdx];
    const currentOrder = current.order;
    const targetOrder = target.order;

    // Optimistically apply the swap so the row doesn't flicker while the two
    // writes and the follow-up refetch settle.
    setLeads((prev) =>
      prev
        .map((l) => {
          if (l.id === current.id) return { ...l, order: targetOrder };
          if (l.id === target.id) return { ...l, order: currentOrder };
          return l;
        })
        .sort((a, b) => a.order - b.order)
    );

    // Persist both swaps together, then surface a single success/failure.
    Promise.all([
      update.mutateAsync({ ...current, order: targetOrder }),
      update.mutateAsync({ ...target, order: currentOrder }),
    ])
      .then(() => saveHandlers.onSuccess())
      .catch(() => saveHandlers.onError());
  };

  const handleTimeSlotChange = (
    value: DateTime | null,
    duration: number | null
  ) => {
    console.log(value, duration);
    updateTimeslot.mutate({
      tripId: props.trip.id,
      timeSlotStart: value?.toISO(),
      duration: duration,
    });
  };

  const leadMatchesFilters = (lead: ILead) => {
    if (!props.showCollectedFilter && !props.showNotPaidFilter) {
      return true;
    }

    const matchesCollected = !props.showCollectedFilter || !!lead.vehicle?.collectedOn;
    const matchesNotPaid =
      !props.showNotPaidFilter || lead.paymentStatus !== PaymentStatus.Sent;

    return matchesCollected && matchesNotPaid;
  };

  useEffect(() => {
    const allLeads = props.leadsFromIds?.data?.leads ?? [];
    const tripLeadIds = props.trip.leads ?? [];

    if (tripLeadIds.length === 0 || allLeads.length === 0) {
      setLeads([]);
      return;
    }

    const inTrip = allLeads.filter((lead: ILead) => tripLeadIds.includes(lead.id));
    setLeads(
      inTrip.sort((a: ILead, b: ILead) => (a.order > b.order ? 1 : -1))
    );
  }, [props.leadsFromIds?.data, props.trip.leads, props.trip.id, props.date]);

  // Cancelled leads aren't collected, so they don't take a pickup number.
  const activePositionById = new Map<string, number>();
  let activeCounter = 0;
  leads.forEach((lead: ILead) => {
    if (lead.status !== LeadStatus["Cancelled"]) {
      activeCounter += 1;
      activePositionById.set(lead.id, activeCounter);
    }
  });

  const renderLeadEvent = (lead: ILead) => (
    <div
      key={lead.id}
      className={classNames(
        leadMatchesFilters(lead) ? "" : "opacity-35 grayscale"
      )}
    >
      <LeadEvent
        lead={lead}
        distances={props.distances}
        handleSelected={() => {}}
        inGroup={leads?.length}
        searchTerm={props.searchTerm}
        handleOrderChange={handleOrderChange}
        tripColour={props.optimisationEnabled ? props.tripColour : undefined}
        displayOrder={
          props.optimisationEnabled ? activePositionById.get(lead.id) : undefined
        }
      />
    </div>
  );

  return (
    <>
      <ConfirmModal
        open={removeOpen}
        setOpen={setRemoveOpen}
        title={"Remove Trip"}
        message={"Are you sure you would like to remove this trip?"}
        confirmButtonText={"Remove"}
        onConfirm={handleRemoveTrip}
        isLoading={false}
      />
      <div
        className={classNames(handleColour(), "w-60 relative p-1 h-full border-r border-gray-200")}
        onClick={() => props.setSelectedTrip(undefined, props.trip.id)}
        ref={drop}
        style={{
          opacity: isOver ? 0.8 : 1,
        }}
      >
        {leads?.length > 0 ? (
          <div className="h-full flex flex-col justify-between text-right">
            <div>
              {leads?.map((lead: ILead) => renderLeadEvent(lead))}
              <span className="w-full px-2 text-gray-400">{`${leads.length} / ${props.capacity}`}</span>
            </div>
            <div>
              <TimeSlotSelect
                trip={props.trip}
                timeSlotChange={handleTimeSlotChange}
                onDriverAvatarClick={() => onDriverAvatarClick(props.trip)}
              />
            </div>
          </div>
        ) : (
          <div className="group relative flex flex-col justify-center items-center h-full min-h-10">
            <div className="flex flex-1 flex-col justify-center items-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setRemoveOpen(true);
                }}
                className="hidden group-hover:block text-2xl bg-red-500 rounded-full text-white hover:bg-red-700"
              >
                <MinusIcon className="h-10 w-10" aria-hidden="true" />
              </button>
              <span className="absolute top-0 right-2 text-gray-400">{`${leads.length} / ${props.capacity}`}</span>
            </div>
            <div className="w-full">
              <TimeSlotSelect
                trip={props.trip}
                timeSlotChange={handleTimeSlotChange}
                onDriverAvatarClick={() => onDriverAvatarClick(props.trip)}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
